import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('toggle', this.#onDetailsToggle, true);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('toggle', this.#onDetailsToggle, true);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Opens the first nested submenu when a mobile top-level drawer item opens.
   * @param {ToggleEvent} event
   */
  #onDetailsToggle = (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement) || !details.open) return;
    if (!this.#isMobileDrawerAccordion(details)) {
      return;
    }

    this.#closeSiblingDetails(details);

    if (!this.#isTopLevelDrawerItem(details)) return;

    this.#openFirstChildSubmenu(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    if (details === this.refs.details) this.#restoreDefaultOpenAccordions();

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Restores accordions that should be open by default after the drawer was reset.
   */
  #restoreDefaultOpenAccordions() {
    const defaultOpenAttribute = window.matchMedia('(max-width: 749px)').matches
      ? 'open-by-default-on-mobile'
      : 'open-by-default-on-desktop';
    const defaultOpenDetails = this.querySelectorAll(`accordion-custom[${defaultOpenAttribute}] > details`);

    defaultOpenDetails.forEach((details) => {
      if (!(details instanceof HTMLDetailsElement)) return;

      details.open = true;
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'true');
    });
  }

  /**
   * @param {HTMLDetailsElement} parentDetails
   */
  #openFirstChildSubmenu(parentDetails) {
    const childMenu = parentDetails.querySelector(':scope > .menu-drawer__menu--childlist');
    if (!(childMenu instanceof HTMLElement)) return;

    const childDetails = this.#getDirectMenuDetails(childMenu);

    childDetails.forEach((details, index) => {
      const shouldOpen = index === 0;

      details.toggleAttribute('open', shouldOpen);
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {boolean}
   */
  #isMobileDrawerAccordion(details) {
    return (
      details !== this.refs.details &&
      this.classList.contains('header__drawer--mobile') &&
      window.matchMedia('(max-width: 749px)').matches
    );
  }

  /**
   * @param {HTMLDetailsElement} details
   * @returns {boolean}
   */
  #isTopLevelDrawerItem(details) {
    const topLevelMenu = this.querySelector(':scope .menu-drawer__navigation > .menu-drawer__menu');
    if (!(topLevelMenu instanceof HTMLElement)) return false;

    return this.#getDirectMenuDetails(topLevelMenu).includes(details);
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #closeSiblingDetails(details) {
    const menu = details.closest('ul.menu-drawer__menu');
    if (!(menu instanceof HTMLElement)) return;

    this.#getDirectMenuDetails(menu).forEach((siblingDetails) => {
      if (siblingDetails === details || !siblingDetails.open) return;

      this.#closeDescendantDetails(siblingDetails);
      reset(siblingDetails);
    });
  }

  /**
   * @param {HTMLElement} menu
   * @returns {HTMLDetailsElement[]}
   */
  #getDirectMenuDetails(menu) {
    return Array.from(menu.children).flatMap((child) => {
      if (!(child instanceof HTMLElement)) return [];

      if (child.tagName === 'ACCORDION-CUSTOM') {
        const details = child.querySelector(':scope > details');
        return details instanceof HTMLDetailsElement ? [details] : [];
      }

      if (child.tagName !== 'LI') return [];

      const directDetails = child.querySelector(':scope > details');
      if (directDetails instanceof HTMLDetailsElement) return [directDetails];

      const accordionDetails = child.querySelector(':scope > accordion-custom > details');
      return accordionDetails instanceof HTMLDetailsElement ? [accordionDetails] : [];
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #closeDescendantDetails(details) {
    details.querySelectorAll('details[open]').forEach((descendantDetails) => {
      if (!(descendantDetails instanceof HTMLDetailsElement)) return;

      reset(descendantDetails);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
